import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, isApiError, type CorporateTemplate, type TemplateSize, type TemplateTrim } from '../lib/api'
import { fileToBase64 } from '../lib/image-upload'
import { navigate, homePath } from '../lib/router'
import { formatMegabytes, templateSizeFromError, templateSizeOf } from '../lib/template-size'
import { ConfirmModal } from './ConfirmModal'
import { PromptModal } from './PromptModal'
import { SlidesPreview } from './BrandingCard'

/** How a size refusal is worded, per the limit the file broke (as BrandingCard). */
const SIZE_KEY = {
  upload: 'tpl_too_large',
  kept: 'tpl_too_large_kept',
  expanded: 'tpl_too_large_expanded',
} as const satisfies Record<TemplateSize['stage'], Key>

/** A refused FILE — worded after "This template was not saved:", as on the bid upload. */
const FILE_REFUSAL_KEY: Record<string, Key> = {
  not_pptx: 'brand_tpl_not_pptx',
  macro_enabled: 'brand_tpl_pptx_macro',
  too_large: 'brand_tpl_too_large',
}

/** A catalogue rule refusing a write (routes/corporate-templates.ts), as a sentence of its own. */
const CATALOGUE_REFUSAL_KEY: Record<string, Key> = {
  name_taken: 'corp_tpl_err_name_taken',
  bad_name: 'corp_tpl_err_bad_name',
  builtin: 'corp_tpl_err_builtin',
  is_default: 'corp_tpl_err_is_default',
  unknown_template: 'corp_tpl_err_unknown',
  bad_master: 'corp_tpl_err_bad_master',
  not_found: 'corp_tpl_err_not_found',
  forbidden: 'corp_tpl_err_forbidden',
}

type Dialog = { kind: 'upload' } | { kind: 'rename'; entry: CorporateTemplate } | { kind: 'retire'; entry: CorporateTemplate }

/**
 * The corporate PowerPoint template catalogue (AB#222) over `/v1/corporate-templates` (AB#220),
 * reached from the account menu's "Corporate templates" — a tenant admin's page. Anyone else gets a
 * "not allowed" message here, and the backend refuses the writes regardless.
 *
 * One table row per template: its name with "Default" / "Built-in" / "Retired" badges, its slide
 * masters, the layout count of the master it renders on, and the actions — Rename (dialog), Make
 * default, Retire (confirmation), plus a "Preview" toggle that expands the theme (palette, fonts,
 * layouts) with the BrandingCard preview, where the slide master is picked. A built-in template is
 * read-only and the default cannot be retired: those buttons stay visible but disabled, described
 * by a line in the row saying why. "Show retired" lists the retired ones too (the server only sends
 * them to a tenant admin).
 *
 * Upload is a dialog (name + file) with the bid upload's behaviour: a file over the limit is refused
 * before it is sent, in plain words; while it uploads the dialog is `aria-busy` with a spinner and
 * every control disabled; a polite live region says "Uploading the PowerPoint template…" then
 * "Template uploaded", and the page says what the server's trim removed.
 *
 * Every write refetches the catalogue AND every template-set view and slides template — a bid that
 * never chose follows a change of default, and its Bid settings preview must show it.
 */
export function CorporateTemplatesPage() {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const ids = useId()
  const headingRef = useRef<HTMLHeadingElement>(null)
  // `me` directly rather than useBidProfile: this page needs to know when it has ANSWERED, so a
  // tenant admin is not shown "not allowed" during the first render.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })
  const isTenantAdmin = me.data?.roles?.includes('admin') ?? false

  const [showRetired, setShowRetired] = useState(false)
  const list = useQuery({
    queryKey: ['corporate-templates', 'slides', showRetired],
    queryFn: () => api.corporateTemplates('slides', showRetired),
    enabled: isTenantAdmin,
  })

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  // The polite live-region message after each action.
  const [status, setStatus] = useState('')
  // Why the last action was refused, shown above the table.
  const [error, setError] = useState('')
  // What the server trimmed off the template just uploaded.
  const [trimmed, setTrimmed] = useState<TemplateTrim | null>(null)

  const sizeText = (size: TemplateSize): string =>
    t(SIZE_KEY[size.stage])
      .replace('{size}', () => formatMegabytes(size.bytes, lang))
      .replace('{limit}', () => formatMegabytes(size.limit, lang))

  const trimText = (trim: TemplateTrim): string =>
    t('tpl_trimmed')
      .replace('{uploaded}', () => formatMegabytes(trim.uploadedBytes, lang))
      .replace('{kept}', () => formatMegabytes(trim.keptBytes, lang))
      .replace('{n}', () => String(trim.removedSlides))

  /** A failed write, in words. */
  function refusalText(e: unknown): string {
    const size = templateSizeFromError(e)
    if (size) return sizeText(size)
    if (!isApiError(e)) return t('corp_tpl_err_generic')
    const fileKey = e.status === 413 ? 'brand_tpl_too_large' : FILE_REFUSAL_KEY[e.code]
    if (fileKey) return `${t('brand_tpl_refused')} ${t(fileKey)}`
    const key = CATALOGUE_REFUSAL_KEY[e.code]
    return key ? t(key) : t('corp_tpl_err_generic')
  }

  const refresh = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['corporate-templates'] }),
      qc.invalidateQueries({ queryKey: ['template-sets'] }),
      qc.invalidateQueries({ queryKey: ['slides-template'] }),
    ])
  }

  /** Run one write: true when it went through; otherwise the page says why (and refetches). */
  async function write(key: string, op: () => Promise<unknown>, done: string): Promise<boolean> {
    setBusy(key)
    setError('')
    setStatus('')
    setTrimmed(null)
    try {
      await op()
      await refresh()
      setStatus(done)
      return true
    } catch (e) {
      setError(refusalText(e))
      // A stale list (someone else retired it, say) is the likeliest cause: show the real one.
      void qc.invalidateQueries({ queryKey: ['corporate-templates'] })
      return false
    } finally {
      setBusy(null)
    }
  }

  /** The upload dialog's submit: null when uploaded, else why not (the dialog shows it). */
  async function upload(name: string, file: File): Promise<string | null> {
    setError('')
    setTrimmed(null)
    setStatus(t('tpl_uploading_slides'))
    setBusy('upload')
    try {
      const base64 = await fileToBase64(file)
      const res = await api.addCorporateTemplate('slides', name, base64)
      await refresh()
      setTrimmed(res.trimmed ?? null)
      setStatus(res.trimmed ? `${t('tpl_uploaded')}. ${trimText(res.trimmed)}` : t('tpl_uploaded'))
      return null
    } catch (e) {
      setStatus('')
      return refusalText(e)
    } finally {
      setBusy(null)
    }
  }

  const toggle = (id: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const header = (
    <div className="home-hero">
      <div className="home-hero-in">
        <button className="rel-back" onClick={() => navigate(homePath)}>
          ← {t('all_bids')}
        </button>
        <h1 ref={headingRef} tabIndex={-1}>
          {t('corp_tpl_title')}
        </h1>
        {isTenantAdmin && <p>{t('corp_tpl_intro')}</p>}
      </div>
    </div>
  )

  if (!me.data) {
    return (
      <main className="homepage" aria-label={t('corp_tpl_title')}>
        {header}
        <div className="corp-tpl">
          <p aria-live="polite">…</p>
        </div>
      </main>
    )
  }

  if (!isTenantAdmin) {
    return (
      <main className="homepage" aria-label={t('corp_tpl_title')}>
        {header}
        <div className="corp-tpl">
          <p className="corp-tpl-denied">{t('corp_tpl_not_allowed')}</p>
        </div>
      </main>
    )
  }

  const templates = list.data?.templates ?? []
  const anyBusy = busy !== null

  return (
    <main className="homepage" aria-label={t('corp_tpl_title')}>
      {header}
      <div className="corp-tpl">
        <p className="visually-hidden" role="status" aria-live="polite">
          {status}
        </p>
        <div className="corp-tpl-bar">
          <button type="button" className="mini ok" disabled={anyBusy} onClick={() => setDialog({ kind: 'upload' })}>
            {t('corp_tpl_upload')}
          </button>
          <label className="corp-tpl-toggle">
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
            {t('corp_tpl_show_retired')}
          </label>
        </div>
        {error && (
          <p className="brand-hint brand-error" role="alert">
            {error}
          </p>
        )}
        {trimmed && <p className="brand-hint">{trimText(trimmed)}</p>}

        {list.isError ? (
          <p className="brand-hint brand-error" role="alert">
            {t('corp_tpl_load_failed')}
          </p>
        ) : !list.data ? (
          <p aria-live="polite">…</p>
        ) : templates.length === 0 ? (
          <p>{t('corp_tpl_empty')}</p>
        ) : (
          <div className="corp-tpl-scroll">
            <table className="kbtable corp-tpl-table">
              <caption className="visually-hidden">{t('corp_tpl_caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('corp_tpl_col_name')}</th>
                  <th scope="col">{t('corp_tpl_col_masters')}</th>
                  <th scope="col">{t('corp_tpl_col_layouts')}</th>
                  <th scope="col">{t('corp_tpl_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.flatMap((entry, i) => {
                  const previewId = `${ids}-preview-${i}`
                  const whyId = `${ids}-why-${i}`
                  const open = expanded.has(entry.id)
                  const master = entry.theme.masters[entry.theme.master] ?? entry.theme.masters[0]
                  const why: Key | null = entry.retired
                    ? 'corp_tpl_why_retired'
                    : entry.builtin
                      ? 'corp_tpl_why_builtin'
                      : entry.default
                        ? 'corp_tpl_why_default'
                        : null
                  const canRename = !entry.builtin && !entry.retired
                  const canRetire = !entry.builtin && !entry.retired && !entry.default
                  const canDefault = !entry.default && !entry.retired
                  const named = (key: Key): string => t(key).replace('{name}', () => entry.name)
                  const rows = [
                    <tr key={entry.id} className={entry.retired ? 'corp-tpl-retired' : undefined}>
                      <td>
                        <div className="corp-tpl-name">
                          <b>{entry.name}</b>
                          {entry.default && <span className="badge cat">{t('corp_tpl_badge_default')}</span>}
                          {entry.builtin && <span className="badge">{t('corp_tpl_badge_builtin')}</span>}
                          {entry.retired && <span className="badge warn">{t('corp_tpl_badge_retired')}</span>}
                        </div>
                      </td>
                      <td>
                        <span>{entry.theme.masters.length}</span>{' '}
                        <span className="kbsrc-muted">{entry.theme.masters.map((m) => m.name).filter(Boolean).join(', ')}</span>
                      </td>
                      <td>{master?.layouts.length ?? 0}</td>
                      <td className="corp-tpl-acts">
                        <div className="kbsrc-acts-row corp-tpl-acts-row">
                          <button
                            type="button"
                            className="mini ghost"
                            aria-expanded={open}
                            aria-controls={previewId}
                            aria-label={named('corp_tpl_preview_of')}
                            onClick={() => toggle(entry.id)}
                          >
                            {open ? '▾' : '▸'} {t('corp_tpl_preview')}
                          </button>
                          <button
                            type="button"
                            className="mini ghost"
                            aria-label={named('corp_tpl_rename_of')}
                            aria-describedby={!canRename && why ? whyId : undefined}
                            disabled={anyBusy || !canRename}
                            onClick={() => setDialog({ kind: 'rename', entry })}
                          >
                            {t('corp_tpl_rename')}
                          </button>
                          <button
                            type="button"
                            className="mini ghost"
                            aria-label={named('corp_tpl_make_default_of')}
                            disabled={anyBusy || !canDefault}
                            onClick={() =>
                              void write(
                                entry.id,
                                () => api.setCorporateTemplateDefault('slides', entry.id),
                                named('corp_tpl_default_set'),
                              )
                            }
                          >
                            {t('corp_tpl_make_default')}
                          </button>
                          <button
                            type="button"
                            className="mini ghost"
                            aria-label={named('corp_tpl_retire_of')}
                            aria-describedby={!canRetire && why ? whyId : undefined}
                            disabled={anyBusy || !canRetire}
                            onClick={() => setDialog({ kind: 'retire', entry })}
                          >
                            {t('corp_tpl_retire')}
                          </button>
                          {busy === entry.id && <span className="brand-spinner" aria-hidden="true" />}
                        </div>
                        {why && (
                          <p id={whyId} className="brand-hint corp-tpl-why">
                            {t(why)}
                          </p>
                        )}
                      </td>
                    </tr>,
                  ]
                  if (open) {
                    rows.push(
                      <tr key={`${entry.id}-preview`} id={previewId} className="corp-tpl-preview-row">
                        <td colSpan={4} aria-busy={busy === entry.id || undefined}>
                          <SlidesPreview
                            theme={entry.theme}
                            canChoose={!entry.builtin && !entry.retired}
                            disabled={anyBusy}
                            onChoose={(m) =>
                              void write(entry.id, () => api.updateCorporateTemplate(entry.id, { master: m }), t('corp_tpl_master_set'))
                            }
                          />
                        </td>
                      </tr>,
                    )
                  }
                  return rows
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialog?.kind === 'upload' && (
        <UploadDialog
          busy={busy === 'upload'}
          sizeText={sizeText}
          onSubmit={async (name, file) => {
            const why = await upload(name, file)
            if (why === null) setDialog(null)
            return why
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'rename' && (
        <PromptModal
          title={t('corp_tpl_rename_title')}
          label={t('corp_tpl_name')}
          initialValue={dialog.entry.name}
          confirmLabel={t('corp_tpl_rename')}
          onSubmit={(name) => {
            const { entry } = dialog
            setDialog(null)
            void write(entry.id, () => api.updateCorporateTemplate(entry.id, { name }), t('corp_tpl_renamed'))
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'retire' && (
        <ConfirmModal
          title={t('corp_tpl_retire_title')}
          message={t('corp_tpl_retire_msg').replace('{name}', () => dialog.entry.name)}
          confirmLabel={t('corp_tpl_retire')}
          danger
          onConfirm={() => {
            const { entry } = dialog
            setDialog(null)
            void write(entry.id, () => api.retireCorporateTemplate(entry.id), t('corp_tpl_retired_done').replace('{name}', () => entry.name)).then(
              (ok) => {
                // The row (and the button that opened the dialog) is gone unless retired ones are
                // listed: put focus somewhere meaningful rather than on <body>.
                if (ok && !showRetired) headingRef.current?.focus()
              },
            )
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  )
}

/**
 * Name + PowerPoint file. The size guard runs on pick (nothing is sent); a server refusal comes back
 * from `onSubmit` and is shown in the dialog, which stays open so the admin can fix and retry.
 * Focus goes to the name field on open and back to the opener on close (as PromptModal); Escape and
 * the backdrop close it, except while the upload runs.
 */
function UploadDialog({
  busy,
  sizeText,
  onSubmit,
  onClose,
}: {
  busy: boolean
  sizeText: (size: TemplateSize) => string
  onSubmit: (name: string, file: File) => Promise<string | null>
  onClose: () => void
}) {
  const { t } = useLang()
  const ids = useId()
  const nameRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    nameRef.current?.focus()
    return () => prev?.focus()
  }, [])

  const close = (): void => {
    if (!busy) onClose()
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (!name.trim() || !file || busy) return
    setError('')
    const why = await onSubmit(name.trim(), file)
    if (why) setError(why)
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close}>
      <div
        className="modal prompt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-busy={busy || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close()
        }}
      >
        <div className="modal-head">
          <h2 id={`${ids}-title`}>{t('corp_tpl_upload_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>
            ✕
          </button>
        </div>
        <form className="modal-body prompt-body" onSubmit={(e) => void submit(e)}>
          <label className="prompt-label">
            {t('corp_tpl_name')}
            <input
              ref={nameRef}
              className="prompt-input"
              value={name}
              maxLength={100}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="prompt-label">
            {t('corp_tpl_file')}
            <input
              type="file"
              accept=".pptx,.potx"
              disabled={busy}
              onChange={(e) => {
                const picked = e.target.files?.[0] ?? null
                const tooLarge = picked ? templateSizeOf('slides', picked.size) : null
                if (tooLarge) {
                  // Said at once, and never sent.
                  e.target.value = ''
                  setFile(null)
                  setError(sizeText(tooLarge))
                  return
                }
                setError('')
                setFile(picked)
              }}
            />
          </label>
          {error && (
            <p className="brand-hint brand-error" role="alert">
              {error}
            </p>
          )}
          <div className="prompt-acts">
            {busy && <span className="brand-spinner" aria-hidden="true" />}
            <button type="button" className="mini ghost" onClick={close} disabled={busy}>
              {t('cancel')}
            </button>
            <button type="submit" className="mini ok" disabled={busy || !name.trim() || !file}>
              {t('corp_tpl_upload_submit')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
